import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';
import {StageRunGraph} from 'workflows/visualization/TaggedNodeGraph';
import {createVirtualNodeGraph, VIRTUAL_NODE_ID_PREFIX} from 'workflows/visualization/VirtualNodeGraph';
import {duplicateBranch} from 'workflows/visualization/VirtualNodeGraphUtil';
import {StageRun} from 'workflows/WorkflowConstants';

import ConfigureStageRunsModal, {BaseConfigureStageRunsModalProps} from './ConfigureStageRunsModal';

type OpenConfigureStageRunsModalProps = Omit<
  BaseConfigureStageRunsModalProps,
  'onRequestClose' | 'onRequestSubmit'
> & {onRequestSubmit?: (numChangedRuns: number) => void};

/**
 * This is a standalone function instead of a static method on ConfigureStageRunsModal,
 * because ConfigureStageRunsModal is connected to getStore().
 */
export function openConfigureStageRunsModal(props: OpenConfigureStageRunsModalProps): void {
  const newProps = {
    ...props,
    onRequestSubmit: props.onRequestSubmit ? props.onRequestSubmit : noop,
    onRequestClose: () => {
      LegacyModalContainer.close(ConfigureStageRunsModal);
    },
  };
  LegacyModalContainer.open(ConfigureStageRunsModal, <ConfigureStageRunsModal {...newProps} />);
}

/**
 * Return whether a new node can be placed on the link.
 */
export function isLinkValidForNode(
  stageRunGraph: StageRunGraph,
  {parentId, childId}: {parentId: string | null; childId: string | null}
): boolean {
  const validParentStatuses = [STAGE_RUN_STATUS.INITIALIZED, STAGE_RUN_STATUS.COMPLETED];
  if (childId && stageRunGraph.getStageRun(childId).status !== STAGE_RUN_STATUS.INITIALIZED) {
    return false;
  } else if (parentId && !validParentStatuses.includes(stageRunGraph.getStageRun(parentId).status)) {
    return false;
  } else {
    return true;
  }
}

export function openConfigureStageRunsModalToBulkCreateStageRuns(
  stageRunGraph: StageRunGraph,
  stageConfigurationIndex: number,
  remainingProps: Omit<OpenConfigureStageRunsModalProps, 'virtualNodeGraph' | 'nodeIdsToEdit'>
): void {
  const links = stageRunGraph.getLinksAcrossTag(stageConfigurationIndex).map((link) => ({
    ...link,
    stageIndex: stageConfigurationIndex,
  }));
  const validLinks = links.filter((link) => isLinkValidForNode(stageRunGraph, link));
  const virtualNodeGraph = createVirtualNodeGraph(stageRunGraph).addNodesOnLinks(validLinks);
  openConfigureStageRunsModal({
    ...remainingProps,
    virtualNodeGraph,
    nodeIdsToEdit: [],
  });
}

export function openConfigureStageRunsModalToEditStageRun(
  stageRunGraph: StageRunGraph,
  stageRun: StageRun,
  remainingProps: Omit<
    OpenConfigureStageRunsModalProps,
    'virtualNodeGraph' | 'nodeIdsToEdit' | 'nodeIdToInputSampleIds' | 'nodeIdToSetParent'
  >
): void {
  const virtualNodeGraph = createVirtualNodeGraph(stageRunGraph);
  const nodeIdToInputSampleIds: {[nodeId: string]: Array<string>} = _.object(
    stageRunGraph.getNodeIds().map((nodeId) => [nodeId, stageRunGraph.getStageRun(nodeId).inputSampleIds])
  );
  openConfigureStageRunsModal({
    ...remainingProps,
    virtualNodeGraph,
    nodeIdsToEdit: [stageRun.id],
    nodeIdToSetParent: stageRun.id,
    nodeIdToInputSampleIds,
  });
}

export function openConfigureStageRunsModalToDuplicateBranch(
  stageRunGraph: StageRunGraph,
  stageRun: StageRun,
  remainingProps: Omit<
    OpenConfigureStageRunsModalProps,
    'virtualNodeGraph' | 'nodeIdsToEdit' | 'nodeIdToInputSampleIds' | 'nodeIdToSetParent'
  >
): void {
  const virtualNodeGraph = createVirtualNodeGraph(stageRunGraph);
  const {newVirtualNodeGraph, newRootNodeId} = duplicateBranch(virtualNodeGraph, stageRun.id);
  const nodeIdToInputSampleIds: {[nodeId: string]: Array<string>} = _.object(
    stageRunGraph.getNodeIds().map((nodeId) => [nodeId, stageRunGraph.getStageRun(nodeId).inputSampleIds])
  );
  openConfigureStageRunsModal({
    ...remainingProps,
    virtualNodeGraph: newVirtualNodeGraph,
    nodeIdsToEdit: [],
    nodeIdToSetParent: newRootNodeId,
    nodeIdToInputSampleIds,
  });
}

export function openConfigureStageRunsModalToCreateStageRun(
  stageRunGraph: StageRunGraph,
  stageConfigurationIndex: number,
  remainingProps: Omit<
    OpenConfigureStageRunsModalProps,
    'virtualNodeGraph' | 'nodeIdsToEdit' | 'nodeIdToSetParent'
  >
): void {
  const virtualNodeGraph = createVirtualNodeGraph(stageRunGraph).addVirtualNodes([
    {
      id: _.uniqueId(VIRTUAL_NODE_ID_PREFIX),
      parentIds: [],
      stageIndex: stageConfigurationIndex,
    },
  ]);
  openConfigureStageRunsModal({
    ...remainingProps,
    virtualNodeGraph,
    nodeIdsToEdit: [],
    nodeIdToSetParent: virtualNodeGraph.getVirtualNodeIds()[0],
  });
}
